/* eslint-disable */
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { useEffect, useState, useMemo } from "react";
import { useSelector } from "react-redux";
import { collection, query, where, Timestamp } from "firebase/firestore";
import { useTranslation } from "react-i18next";
import { color } from "framer-motion";
import { db } from "../../../../firebase";
import workerAPI from "../../../../api/worker/worker_service";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const getDateRange = (startDate, endDate) => {
  const dates = [];
  let currentDate = new Date(startDate);

  while (currentDate <= endDate) {
    dates.push(new Date(currentDate));
    currentDate.setDate(currentDate.getDate() + 1);
  }

  return dates;
};
const isDark = document.documentElement.classList.contains("dark");

const groupOrdersByDate = (orders, filter) => {
  // Déterminer la plage de dates en fonction du filtre
  const now = new Date();
  const startDate = new Date(now);

  if (filter === "weekly") {
    startDate.setDate(now.getDate() - 7);
  } else {
    // monthly
    startDate.setMonth(now.getMonth() - 1);
  }

  // Créer un objet avec toutes les dates dans la plage, initialisées à 0
  const allDates = getDateRange(startDate, now);
  const dateFormatOptions = {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  };

  const groupedOrders = allDates.reduce((acc, date) => {
    const dateStr = date.toLocaleDateString("fr-FR", dateFormatOptions);
    acc[dateStr] = { status20: 0, others: 0 };
    return acc;
  }, {});

  // Remplir avec les données réelles
  orders.forEach((order) => {
    let orderTime = null;
    
    // Gérer les différents formats de timestamp
    if (order.timeStamp?._seconds) {
      orderTime = new Date(order.timeStamp._seconds * 1000);
    } else if (order.timeStamp?.seconds) {
      orderTime = new Date(order.timeStamp.seconds * 1000);
    }
    
    if (orderTime) {
      const orderDateStr = orderTime.toLocaleDateString(
        "fr-FR",
        dateFormatOptions
      );

      // Vérifier si la date est dans notre plage
      if (orderTime >= startDate && orderTime <= now) {
        if (!groupedOrders[orderDateStr]) {
          groupedOrders[orderDateStr] = { status20: 0, others: 0 };
        }

        if (
          order.deleveryStatus === "20" ||
          order.firstDeleveryStatus === "20"
        ) {
          groupedOrders[orderDateStr].status20 += 1;
        } else {
          groupedOrders[orderDateStr].others += 1;
        }
      }
    }
  });

  // Trier les dates
  const sortedEntries = Object.entries(groupedOrders).sort(
    ([dateA], [dateB]) => {
      const [dayA, monthA, yearA] = dateA.split("/").map(Number);
      const [dayB, monthB, yearB] = dateB.split("/").map(Number);
      return (
        new Date(yearA, monthA - 1, dayA) - new Date(yearB, monthB - 1, dayB)
      );
    }
  );

  return sortedEntries;
};

const AudienceGrowthChart = () => {
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState("weekly");
  const account = useSelector((state) => state.AccountReducer);
  const { t , i18n } = useTranslation();

  useEffect(() => {
    if (!account) return;

    const fetchOrders = async () => {
      try {
        const now = new Date();
        let startDate = new Date(now);

        if (filter === "weekly") {
          startDate.setDate(now.getDate() - 7);
        } else {
          // monthly
          startDate.setMonth(now.getMonth() - 1);
        }

        const startTimestamp = Timestamp.fromDate(startDate);
        const endTimestamp = Timestamp.fromDate(now);

        const fetchedOrders = await workerAPI.getOrders(
          startTimestamp,
          endTimestamp
        );

        const formattedOrders = fetchedOrders
          .filter((order) => order.deleted !== 1)
          .sort((a, b) => {
            const timeA = a.timeStamp?._seconds || a.timeStamp?.seconds || 0;
            const timeB = b.timeStamp?._seconds || b.timeStamp?.seconds || 0;
            return timeB - timeA;
          });

        if ((account?.productMode ?? "classic") === "classic") {
          const classicOrders = formattedOrders.filter(
            (order) => order.type !== "digitalProducts"
          );
          setOrders(classicOrders);
        } else {
          const digitalOrders = formattedOrders.filter(
            (order) => order.type === "digitalProducts"
          );
          setOrders(digitalOrders);
        }
      } catch (error) {
        console.error("Error fetching orders:", error);
      }
    };

    fetchOrders();
  }, [filter, account]);

  const { labels, ordersCount, abandonedOrdersCount } = useMemo(() => {
    const sortedEntries = groupOrdersByDate(orders, filter);

    return {
      labels: sortedEntries.map(([date]) => date),
      ordersCount: sortedEntries.map(([, value]) => value.others || 0),
      abandonedOrdersCount: sortedEntries.map(
        ([, value]) => value.status20 || 0
      ),
    };
  }, [orders, filter]);

  // Calculate totals
  const totalOrders = ordersCount.reduce((sum, count) => sum + count, 0);
  const totalAbandoned = abandonedOrdersCount.reduce(
    (sum, count) => sum + count,
    0
  );
  const data = {
    labels,
    datasets: [
      {
        label: t("dashboard.bigChart.orders"),
        data: ordersCount,
        borderColor: "#3B82F6",
        backgroundColor: "rgba(59, 130, 246, 0.1)",
        tension: 0.4,
        borderWidth: 2,
        pointBackgroundColor: "#3B82F6",
        pointBorderColor: "#fff",
        pointBorderWidth: 1,
        pointRadius: 4,
        pointHoverRadius: 6,
      },
      {
        label: t("dashboard.bigChart.abondonneds"),
        data: abandonedOrdersCount,
        borderColor: "#F59E0B",
        backgroundColor: "rgba(245, 158, 11, 0.1)",
        tension: 0.4,
        borderWidth: 2,
        pointBackgroundColor: "#F59E0B",
        pointBorderColor: "#fff",
        pointBorderWidth: 1,
        pointRadius: 4,
        pointHoverRadius: 6,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false, // Hide the legend/labels at the top
      },
      tooltip: {
        mode: "index",
        intersect: false,
        backgroundColor: "rgba(255, 255, 255, 0.9)",
        titleColor: "#444",
        bodyColor: "#666",
        borderColor: "#ddd",
        borderWidth: 1,
        padding: 10,
        boxPadding: 6,
        usePointStyle: true,
        callbacks: {
          label: function (context) {
            return `${context.dataset.label}: ${context.parsed.y}`;
          },
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          color: isDark ? '#FFFFFF' : '#000000',
          font: {
            size: 10,
          },
        },
      },
      y: {
        beginAtZero: true,
        position: i18n.dir() === "rtl" ? "right" : "left",
        ticks: {
          stepSize: 1,
          font: {
            size: 10,
          },
        },
        grid: {
          borderDash: [4, 4],
        },
      },
    },
    interaction: {
      mode: "index",
      intersect: false,
    },
    elements: {
      line: {
        fill: true,
      },
    },
  };

  return (
    <div className="bg-white dark:!bg-[#181919] col-span-1 md:col-span-3 rounded-lg shadow-sm w-full h-[400px] dark:border dark:border-[#3c3a3d]">
      <div className="p-4" >
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-bold text-gray-800 dark:text-white m-0">{t("dashboard.bigChart.analyse")}</h3>
          <div className="mr-3 flex space-x-4 rtl:space-x-reverse">
            <div className="flex items-center gap-x-2">
              <span className="w-3 h-3 rounded-full bg-blue-500 mr-1"></span>
              <span className="text-xs text-gray-600 dark:text-white">{totalOrders} {t("dashboard.bigChart.order")}</span>
            </div>
            <div className="flex items-center gap-x-2">
              <span className="w-3 h-3 rounded-full bg-amber-500 mr-1"></span>
              <span className="text-xs text-gray-600 dark:text-white">
                {totalAbandoned} {t("dashboard.bigChart.abondonned")}
              </span>
            </div>
          </div>
          <select
            className="border border-gray-200 dark:!border-[#3c3a3d] dark:bg-[#141414] px-3 py-1.5 rounded-lg text-gray-700 dark:text-white text-sm bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent cursor-pointer"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          >
            <option value="weekly">{t("dashboard.bigChart.semaine")}</option>
            <option value="monthly">{t("dashboard.bigChart.mois")}</option>
          </select>
        </div>

        <div className="w-full" style={{ height: "300px" }}>
          <Line data={data} options={options} />
        </div>
      </div>
    </div>
  );
};

export default AudienceGrowthChart;
